import type { SvgIconComponent } from "@mui/icons-material";
import { type FC, useMemo } from "react";
import { NavLink } from "react-router";

import { RichTooltip } from "~/components/UI/RichTooltip";
import {
  bgColorByEventTagEnum,
  colorByEventTagEnum,
  type EventTagValue,
  glyphByEventTagEnum,
  labelByEventTagEnum,
  routeByEventTagEnum,
  rowBgColorByEventTagEnum,
  rowColorByEventTagEnum,
  rowGlyphByEventTagEnum,
  rowTooltipByEventTagEnum,
} from "~/types/tag";

import * as Styled from "./CategoryChip.styles";

interface ICategoryChipProps {
  tag?: EventTagValue;
  appearance?: "list" | "row";
}

export const TagChip: FC<ICategoryChipProps> = ({
  tag,
  appearance = "list",
}) => {
  const tabName = useMemo(() => (tag ? labelByEventTagEnum[tag] : ""), [tag]);

  const { tagColor, bgTagColor } = useMemo(() => {
    if (!tag) {
      return { tagColor: "primary", bgTagColor: "primary" };
    }

    const isRow = appearance === "row";

    return {
      tagColor:
        (isRow && rowColorByEventTagEnum[tag]) || colorByEventTagEnum[tag],
      bgTagColor:
        (isRow && rowBgColorByEventTagEnum[tag]) || bgColorByEventTagEnum[tag],
    };
  }, [appearance, tag]);

  const glyph = useMemo(() => {
    if (!tag) {
      return "";
    }

    return (
      (appearance === "row" ? rowGlyphByEventTagEnum[tag] : undefined) ??
      glyphByEventTagEnum[tag]
    );
  }, [appearance, tag]);
  const stringPrefix = typeof glyph === "string" ? glyph : "";
  const IconComponent =
    typeof glyph !== "string" && glyph ? (glyph as SvgIconComponent) : null;

  if (!tag || !tabName) {
    return null;
  }

  const chip = (
    <Styled.StyledChip
      icon={IconComponent ? <IconComponent /> : undefined}
      label={`${stringPrefix}${tabName}`}
      component={NavLink}
      to={routeByEventTagEnum[tag]}
      prefetch="intent"
      $color={tagColor}
      $bgColor={bgTagColor}
      $rowStyle={appearance === "row" ? "badge" : undefined}
      size="small"
      variant="outlined"
    />
  );

  const tooltip =
    appearance === "row" ? rowTooltipByEventTagEnum[tag] : undefined;

  return tooltip ? <RichTooltip title={tooltip}>{chip}</RichTooltip> : chip;
};
